import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap, catchError, switchMap, filter, take } from 'rxjs/operators';
import { throwError, BehaviorSubject, Observable } from 'rxjs';
import { Authentication } from '../services/authentication';
import { HTTP_STATUS_CODE, LanguageUtil } from '@shared/utils';
import { APP_CONSTANTS } from '../constants/app';

// Variable global para controlar el proceso de refresh
let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

/**
 * HTTP Interceptor funcional que maneja autenticación JWT y configuración de headers
 */
export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  // Inyección de dependencias usando inject()
  const router = inject(Router);
  const auth = inject(Authentication);

  const token = auth.getToken();
  let modifiedRequest = req;

  /**
   * Si existe el token, es agregado a la cabecera junto con el
   * idioma por defecto el cual es español 'es', en caso de no haber, no se envía el token
   */
  if (token) {
    modifiedRequest = req.clone({
      setHeaders: {
        'Accept-Language': LanguageUtil.getStorageLanguage('es'),
        Authorization: headerAuthorization(token),
      },
    });
  } else {
    modifiedRequest = req.clone({
      setHeaders: {
        'Accept-Language': LanguageUtil.getStorageLanguage('es'),
      },
    });
  }

  return next(modifiedRequest).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
          // Si el token expiró, intentar refrescarlo
          return handleUnauthorizedError(req, next, auth, router);
        }
      }
      return throwError(() => error);
    }),
  );
};

/**
 * Maneja errores de autenticación 401 intentando refrescar el token
 * @param req - Request HTTP original
 * @param next - Handler para continuar con la petición
 * @param auth - Servicio de autenticación
 * @param router - Router de Angular
 * @returns Observable con la respuesta HTTP
 */
function handleUnauthorizedError(
  req: any,
  next: any,
  auth: Authentication,
  router: Router,
): Observable<any> {
  // Si ya se está refrescando el token, esperar a que termine
  if (isRefreshing) {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null), // Esperar a que el token nuevo esté disponible
      take(1), // Tomar solo el primer valor
      switchMap((token) => {
        // Reintentar la petición original con el nuevo token
        return next(addTokenToRequest(req, token!));
      }),
    );
  }

  // Iniciar el proceso de refresh
  isRefreshing = true;
  refreshTokenSubject.next(null);

  const refreshToken = auth.getRefreshToken();

  if (!refreshToken) {
    // No hay refresh token, hacer logout
    isRefreshing = false;
    auth.logout();
    return throwError(() => new Error('No refresh token available'));
  }

  // Intentar refrescar el token
  return auth.refresh(refreshToken).pipe(
    switchMap((tokenResponse) => {
      // Refresh exitoso
      isRefreshing = false;
      const newToken = tokenResponse.access;
      refreshTokenSubject.next(newToken);

      // Reintentar la petición original con el nuevo token
      return next(addTokenToRequest(req, newToken));
    }),
    catchError((refreshError) => {
      // El refresh falló, hacer logout
      isRefreshing = false;
      refreshTokenSubject.next(null);
      auth.logout();
      return throwError(() => refreshError);
    }),
  );
}

/**
 * Agrega el token de autorización a una petición HTTP
 * @param req - Request HTTP
 * @param token - Token de autorización
 * @returns Request HTTP clonado con el token agregado
 */
function addTokenToRequest(req: any, token: string): any {
  return req.clone({
    setHeaders: {
      'Accept-Language': LanguageUtil.getStorageLanguage('es'),
      Authorization: headerAuthorization(token),
    },
  });
}

/**
 * Función helper que agrega el token de autorización a la cabecera
 * @param token: Recibe el token con el que se armará la cabecera
 * @return Retorna la clave Api-key y el token para armar la cabecera
 */
function headerAuthorization(token: string): string {
  return `${APP_CONSTANTS.NAME_TOKEN_HEADER} ${token}`;
}
